import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import voidImg from '../Images/void.svg';
import Spinner from '../Modal/Spinner';
import { formatDistanceToNow } from 'date-fns';
import { enUS, srLatn } from 'date-fns/locale';
import {
  fetchNotifications,
  markAllNotificationsAsRead,
  markNotificationAsRead,
} from '../../../Header/Notifications/notificationsApi';
import './Notifications.css';

const PREVIEW_LIMIT = 90;

const getDateLocale = (language) =>
  String(language || '')
    .toUpperCase()
    .startsWith('EN')
    ? enUS
    : srLatn;

const getPreview = (text) => {
  const normalized = String(text || '').trim();
  if (normalized.length <= PREVIEW_LIMIT) return normalized;
  return `${normalized.slice(0, PREVIEW_LIMIT).trim()}…`;
};

function NotificationCard({ option, onSelect, onOpenMessage, formatTime }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const fullText = String(option.fullMessage || option.subtitle || '').trim();
  const isLong = fullText.length > PREVIEW_LIMIT;

  return (
    <div
      className={`not-tab-item-wrapper ${
        option.read ? 'is-read' : 'is-unread'
      }`}
      style={{ backgroundColor: option.read ? '#fff' : '#fff8f0' }}
      onClick={() => onSelect?.(option)}
    >
      <div className="not-card">
        <div className="not-card-top">
          <div className="not-card-icon">
            <i className={option.icon || 'fas fa-bell'} />
          </div>
          <div className="not-card-body">
            <div className="not-card-row">
              <span className="not-card-title">{option.title}</span>
              <span className="not-card-time">{formatTime(option.time)}</span>
            </div>
            {fullText && (
              <p className="not-card-message">
                {expanded || !isLong ? fullText : getPreview(fullText)}
              </p>
            )}
            <div className="not-card-actions">
              {isLong && (
                <button
                  type="button"
                  className="not-card-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    const next = !expanded;
                    setExpanded(next);
                    if (next) onOpenMessage?.(option);
                  }}
                >
                  {expanded
                    ? t('notifications.showLess')
                    : t('notifications.readMore')}
                </button>
              )}
              {/* Otvaranje taba privremeno isključeno — samo pročitaj */}
            </div>
          </div>
          {!option.read && <span className="not-card-dot" />}
        </div>
      </div>
    </div>
  );
}

function NotificationsTabList() {
  const { t, i18n } = useTranslation();
  const [notificationsData, setNotificationsData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showUnread, setShowUnread] = useState(false);

  const formatTime = useCallback(
    (value) => {
      if (!value) return '';
      try {
        return formatDistanceToNow(new Date(value), {
          locale: getDateLocale(i18n.language),
        });
      } catch {
        return '';
      }
    },
    [i18n.language],
  );

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      const result = await fetchNotifications({
        unreadOnly: showUnread,
        offset: 0,
        next: 100,
      });
      setNotificationsData(result);
      setIsLoading(false);
    };
    load();
  }, [showUnread]);

  const markReadLocal = useCallback(
    async (notification) => {
      if (notification.read) return;
      const ok = await markNotificationAsRead(notification.id);
      if (!ok) return;

      if (showUnread) {
        setNotificationsData((prev) =>
          prev.filter((item) => item.id !== notification.id),
        );
      } else {
        setNotificationsData((prev) =>
          prev.map((item) =>
            item.id === notification.id ? { ...item, read: true } : item,
          ),
        );
      }
    },
    [showUnread],
  );

  const onReadAll = useCallback(async () => {
    const ok = await markAllNotificationsAsRead();
    if (!ok) return;
    if (showUnread) {
      setNotificationsData([]);
    } else {
      setNotificationsData((prev) =>
        prev.map((item) => ({ ...item, read: true })),
      );
    }
  }, [showUnread]);

  const onOpenMessage = useCallback(
    async (notification) => {
      await markReadLocal(notification);
    },
    [markReadLocal],
  );

  return (
    <div className="not-tab-list-wrapper">
      {isLoading && <Spinner />}
      <div className="not-header-wrapper" style={{ position: 'static' }}>
        <span className="not-header-title">{t('notifications.title')}</span>
        <span className="not-header-text" onClick={onReadAll}>
          {t('notifications.markAllAsRead')}
        </span>
      </div>
      <div className="not-filter-row" style={{ paddingBottom: 12 }}>
        <button
          type="button"
          className={`not-filter-btn ${!showUnread ? 'active' : ''}`}
          onClick={() => setShowUnread(false)}
        >
          {t('all')}
        </button>
        <button
          type="button"
          className={`not-filter-btn ${showUnread ? 'active' : ''}`}
          onClick={() => setShowUnread(true)}
        >
          {t('notifications.unread')}
        </button>
      </div>

      <div className="not-list-wrapper" style={{ maxHeight: 'none' }}>
        {notificationsData.length > 0 ? (
          notificationsData.map((option) => (
            <NotificationCard
              key={option.id}
              option={option}
              onSelect={onOpenMessage}
              onOpenMessage={onOpenMessage}
              formatTime={formatTime}
            />
          ))
        ) : (
          <div className="not-no-data-wrapper">
            <img src={voidImg} alt="No data" width="120px" />
            <span className="not-no-data-span">
              {showUnread
                ? t('notifications.noUnread')
                : t('notifications.empty')}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

export default NotificationsTabList;
